import React, { useState, useContext, useEffect, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeContext } from '../../../../context/ThemeContext';
import { usePageContext } from '../../../../context/PageContext';
import { useAuthModal } from '../../../../context/AuthModalContext';
import { API_URL, buildBackendImgUrl } from '../../../../utils/api';
import {
  NavBar, NavContainer, LeftSection, Logo, Tagline,
  MenuButton, HamburgerMenu, UserChip, ChipAvatar,
  MsgBtn, MsgBadge, RightSection, DrawerBackdrop,
  DrawerSection, DrawerLabel, DrawerItem, DrawerBtn,
  DrawerFooter, DrawerLogout, DrawerBadge,
} from './Navbar.styles';

const ModalLogin     = lazy(() => import('../../../Modal/ModalLogin/ModalLogin'));
const ContactContent = lazy(() => import('../../../Content/ContactContent'));
const AboutContent   = lazy(() => import('../../../Content/AboutContent'));

function Navbar({ user, onLogout, onLogin, onToggleMensajes }) {
  const [click,             setClick]             = useState(false);
  const [showContactModal,  setShowContactModal]  = useState(false);
  const [showAboutModal,    setShowAboutModal]    = useState(false);
  const [noLeidos,          setNoLeidos]          = useState(0);
  const { openAuthModal } = useAuthModal();
  const navigate    = useNavigate();
  const location    = useLocation();
  const { theme }   = useContext(ThemeContext);
  const { badge, logo } = usePageContext();
  const acc         = theme.accent || '#667eea';
  const handleClick = () => setClick(!click);
  const closeMobileMenu = () => setClick(false);
  const isActive = (path) => location.pathname === path;


  // Polling no-leídos cada 30s cuando hay sesión
  useEffect(() => {
    if (!user) { setNoLeidos(0); return; }
    const fetchNoLeidos = () => {
      const token = localStorage.getItem('trastero_token');
      fetch(`${API_URL}/api/mensajes/no-leidos`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : { total: 0 })
        .then(d => setNoLeidos(d.total || 0))
        .catch(() => {});
    };
    fetchNoLeidos();
    const interval = setInterval(fetchNoLeidos, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = () => {
    onLogout();
    closeMobileMenu();
    navigate('/');
  };

  
  return (
    <>


      <Suspense fallback={null}>
        <ModalLogin isOpen={showAboutModal} onClose={() => setShowAboutModal(false)}>
          <AboutContent />
        </ModalLogin>
      </Suspense>

      <Suspense fallback={null}>
        <ModalLogin isOpen={showContactModal} onClose={() => setShowContactModal(false)}>
          <ContactContent onClose={() => setShowContactModal(false)} />
        </ModalLogin>
      </Suspense>

      <NavBar navbarColor={theme.navbar} accentColor={theme.accent}>
        <NavContainer>
          <LeftSection>
            {(() => {
              const deMatch = location.pathname.match(/^\/de\/([^/?]+)/i);
              const deNombre = deMatch ? decodeURIComponent(deMatch[1]) : null;
              return (
                <Logo to="/" onClick={closeMobileMenu} style={{ display: 'flex', alignItems: 'end', gap: 8 }}>
                  {deNombre ? (
                    <>
                    {logo
                      ? <img 
                          src={buildBackendImgUrl(logo)} 
                          alt="" 
                          style={{ width: 70, height: 49, borderRadius: 6, objectFit: 'contain', flexShrink: 0 }} 
                        />
                      : <img 
                          src="/Logo.svg" 
                          alt="Trastero" 
                          style={{ width: 70, height: 49, objectFit: 'contain', flexShrink: 0 }} 
                        />
                    }
                      <span style={{ color: acc }}>{deNombre}</span>
                    </>
                  ) : (
                    <>
                      <img src="/logo.svg" alt="Trastero" style={{ width: 70, height: 49, objectFit: 'contain', flexShrink: 0 }} />
                      <span style={{ color: acc }}>TrasteroWeb</span>
                    </>
                  )}
                </Logo>
              );
            })()}
          </LeftSection>

          {location.pathname === '/' && (
            <Tagline>
              <div style={{ fontSize: 'clamp(13px, 1.4vw, 18px)', fontWeight: 800, color: 'var(--text-color)', lineHeight: 1.2 }}>
                Crea tu Trastero · Garaje · Bodega · Rastro
              </div>
              <div style={{ fontSize: 'clamp(10px, 0.9vw, 12px)', color: 'var(--text-color)', opacity: 0.5, marginTop: 2 }}>
                Compra, vende o intercambia artículos de segunda mano
              </div>
              <div style={{ fontSize: 'clamp(9px, 0.8vw, 11px)', color: 'var(--accent-color)', opacity: 0.8, marginTop: 1, fontWeight: 600 }}>
                Gratuito · Sin intermediarios · Entre particulares
              </div>
            </Tagline>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {badge && (
              <span style={{ fontSize: 13, fontWeight: 600, color: acc, opacity: 0.85, whiteSpace: 'nowrap' }}>
                {badge}
              </span>
            )}
            {user && (
              <UserChip style={{ cursor: 'pointer', gap: '8px' }} title="Mi Trastero">
                <ChipAvatar
                  src={buildBackendImgUrl(user.avatar)} alt={user.name}
                  onClick={() => navigate('/mi-trastero')}
                />
                <span onClick={() => navigate('/mi-trastero')}>{user.name}</span>
                <MsgBtn onClick={onToggleMensajes} title="Mensajes">
                  💬
                  {noLeidos > 0 && <MsgBadge accent={theme.accent}>{noLeidos > 9 ? '9+' : noLeidos}</MsgBadge>}
                </MsgBtn>
              </UserChip>
            )}
            <HamburgerMenu onClick={handleClick}>{click ? '✕' : '☰'}</HamburgerMenu>
          </div>
        </NavContainer>
      </NavBar>

      {/* Drawer lateral — desliza de derecha a izquierda, debajo del navbar */}
      <DrawerBackdrop active={click} onClick={closeMobileMenu} />
      <RightSection active={click} accent={acc}>

        {/* NAVEGACIÓN */}
        {user && (
          <DrawerSection>
            <DrawerLabel>NAVEGACIÓN</DrawerLabel>
            <DrawerItem to="/" active={isActive('/')} accent={acc} onClick={closeMobileMenu}>
              🏠 Inicio
            </DrawerItem>
            <DrawerBtn active={false} accent={acc} onClick={() => { onToggleMensajes(); closeMobileMenu(); }}>
              💬 Mensajes
              {noLeidos > 0 && <DrawerBadge accent={acc}>{noLeidos > 9 ? '9+' : noLeidos}</DrawerBadge>}
            </DrawerBtn>
            <DrawerItem to="/mi-perfil" active={isActive('/mi-perfil')} accent={acc} onClick={closeMobileMenu}>
              👤 Mi Perfil
            </DrawerItem>
            <DrawerItem to="/mi-trastero" active={isActive('/mi-trastero')} accent={acc} onClick={closeMobileMenu}>
              📦 Mi Trastero
            </DrawerItem>
          </DrawerSection>
        )}

        {/* CONFIGURACIÓN */}
        <DrawerSection>
          <DrawerLabel>CONFIGURACIÓN</DrawerLabel>
          <DrawerItem to="/settings" active={isActive('/settings')} accent={acc} onClick={closeMobileMenu}>
            ⚙️ Tema
          </DrawerItem>
          <DrawerBtn active={false} accent={acc} onClick={() => { setShowAboutModal(true); closeMobileMenu(); }}>
            ℹ️ Acerca de
          </DrawerBtn>
          <DrawerBtn active={false} accent={acc} onClick={() => { setShowContactModal(true); closeMobileMenu(); }}>
            ✉️ Contacto
          </DrawerBtn>
        </DrawerSection>

        {/* Sin sesión */}
        {!user && (
          <DrawerSection>
            <MenuButton accentColor={acc} onClick={() => { openAuthModal('signin'); closeMobileMenu(); }}
              style={{ margin: '4px 16px', width: 'calc(100% - 32px)' }}>
              Iniciar Sesión
            </MenuButton>
            <MenuButton accentColor={acc} onClick={() => { openAuthModal('signup'); closeMobileMenu(); }}
              style={{ margin: '4px 16px', width: 'calc(100% - 32px)', background: 'transparent' }}>
              Registrarse
            </MenuButton>
          </DrawerSection>
        )}
        {/* Pie: Salir */}
        {user && (
          <DrawerFooter>
            <DrawerLogout onClick={handleLogout}>↪ Salir</DrawerLogout>
          </DrawerFooter>
        )}
      </RightSection>
    </>
  );
} 
export default Navbar;
